/**
 * Error Reporting System - Configuration
 *
 * Centralized configuration for:
 * - Breadcrumb limits
 * - Severity classification rules (pattern matching)
 * - Email notification settings
 * - Retention policies
 * - Fingerprint generation rules
 *
 * @see TASKS/working/ERROR-REPORTING-SYSTEM-PLAN.md
 */

import type { Severity, ErrorType } from './types';

// =============================================================================
// MAIN CONFIGURATION
// =============================================================================

export const ERROR_REPORTING_CONFIG = {
  /**
   * Whether error reporting is enabled.
   * Can be toggled via environment variable for gradual rollout.
   */
  enabled: process.env.ENABLE_ERROR_REPORTING !== 'false',

  // ===========================================================================
  // BREADCRUMB SETTINGS
  // ===========================================================================

  /**
   * Maximum number of breadcrumbs to keep per error.
   * ~30 breadcrumbs ≈ 5 minutes of user activity.
   */
  maxBreadcrumbs: 30,

  /**
   * Whether to automatically capture console.log/warn/error as breadcrumbs.
   * Disable in development to reduce noise.
   */
  captureConsole: process.env.NODE_ENV === 'production',

  /**
   * Whether to automatically capture click events as breadcrumbs.
   */
  captureClicks: true,

  /**
   * Whether to automatically capture navigation events as breadcrumbs.
   */
  captureNavigation: true,

  /**
   * Whether to automatically capture fetch/XHR requests as breadcrumbs.
   */
  captureHttp: true,

  // ===========================================================================
  // RETENTION
  // ===========================================================================

  /**
   * Days to keep error reports before automatic cleanup.
   */
  retentionDays: 30,

  // ===========================================================================
  // SEVERITY CLASSIFICATION
  // ===========================================================================

  /**
   * Rules for automatically determining error severity.
   * Patterns are tested against error messages (case-insensitive).
   */
  severityRules: {
    /**
     * CRITICAL: Security issues, payment failures, unhandled crashes.
     * Triggers immediate email notification.
     */
    critical: {
      emailImmediately: true,
      patterns: [
        // Security-related
        /rls.*policy/i,
        /row.?level.?security/i,
        /authentication/i,
        /unauthorized/i,
        /forbidden/i,
        /permission.?denied/i,

        // Payment/billing (patterns avoid false positives from route paths like /settings/billing)
        /payment.*(fail|error|declined|invalid)/i,
        /stripe/i,
        /billing.*(fail|error|invalid|expired)/i,
        /checkout.*(fail|error|invalid)/i,
        /subscription.*(fail|error|expired|cancel)/i,

        // Database fatal errors
        /FATAL/i,
        /PANIC/i,

        // Unhandled errors
        /unhandled.*rejection/i,
        /uncaught.*exception/i,
      ],
      /**
       * Error types that are always critical regardless of message.
       */
      errorTypes: ['client_runtime'] as ErrorType[],
    },

    /**
     * ERROR: Standard server/client errors.
     * Included in daily digest email.
     */
    error: {
      emailImmediately: false,
      emailInDigest: true,
      patterns: [] as RegExp[], // Default for non-critical errors
    },

    /**
     * WARNING: Validation errors, rate limits, timeouts.
     * Logged but not emailed (dashboard only).
     */
    warning: {
      emailImmediately: false,
      emailInDigest: false,
      patterns: [
        /validation/i,
        /invalid/i,
        /required/i,
        /rate.?limit/i,
        /too.?many.?requests/i,
        /timeout/i,
        /timed.?out/i,
        /429/,
        // Realtime disconnections are expected on flaky networks/tab sleep/JWT expiry.
        // They do not affect core app functionality — downgrade to warning. (NEXTJS-62)
        /realtime.+connection.+failed/i,
        /realtime.+retries/i,
      ],
    },
  },

  // ===========================================================================
  // EMAIL SETTINGS
  // ===========================================================================

  email: {
    /**
     * Developer email to receive error notifications.
     * Should be set via environment variable.
     */
    to: process.env.DEV_ERROR_EMAIL || process.env.DEV_EMAIL || '',

    /**
     * From address for error notification emails.
     * Must be verified in your email provider (Resend).
     */
    from: process.env.ERROR_EMAIL_FROM || 'errors@cmgworkflow.com',

    /**
     * Prefix for email subjects.
     */
    subjectPrefix: '[CMG Workflow CRM Error]',

    /**
     * Whether to send a daily digest of non-critical errors.
     */
    sendDailyDigest: true,

    /**
     * Hour (UTC) to send daily digest.
     */
    dailyDigestHour: 9,
  },

  // ===========================================================================
  // FINGERPRINTING
  // ===========================================================================

  /**
   * Fields used to generate error fingerprint for grouping.
   * Similar errors (same fingerprint) are grouped together.
   */
  fingerprintFields: ['errorType', 'errorName', 'errorMessage', 'environment.route'] as const,

  /**
   * Maximum length of error message to use in fingerprint.
   * Truncating prevents minor variations from creating new groups.
   */
  fingerprintMessageMaxLength: 100,

  // ===========================================================================
  // RATE LIMITING (Client-side API)
  // ===========================================================================

  /**
   * Maximum client error reports per IP per minute.
   * Prevents spam/abuse of the error reporting endpoint.
   */
  clientRateLimit: 10,

  /**
   * Rate limit window in seconds.
   */
  clientRateLimitWindow: 60,

  // ===========================================================================
  // API ENDPOINT
  // ===========================================================================

  /**
   * Path to the client error report API endpoint.
   */
  apiEndpoint: '/api/error-reports',
} as const;

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Determine the severity of an error based on configuration rules.
 *
 * @param errorMessage - The error message to classify
 * @param errorType - The type of error
 * @returns The determined severity level
 */
export function determineSeverity(errorMessage: string, errorType: ErrorType): Severity {
  const { severityRules } = ERROR_REPORTING_CONFIG;
  const messageLower = errorMessage.toLowerCase();

  // Check if error type is always critical
  if (severityRules.critical.errorTypes.includes(errorType)) {
    return 'critical';
  }

  // Check critical patterns
  for (const pattern of severityRules.critical.patterns) {
    if (pattern.test(messageLower)) {
      return 'critical';
    }
  }

  // Check warning patterns
  for (const pattern of severityRules.warning.patterns) {
    if (pattern.test(messageLower)) {
      return 'warning';
    }
  }

  // Default to error
  return 'error';
}

/**
 * Check if an error should trigger immediate email notification.
 *
 * @param severity - The severity level
 * @returns Whether to send email immediately
 */
export function shouldEmailImmediately(severity: Severity): boolean {
  return ERROR_REPORTING_CONFIG.severityRules[severity].emailImmediately;
}

/**
 * Check if error reporting is enabled.
 *
 * @returns Whether error reporting is enabled
 */
export function isErrorReportingEnabled(): boolean {
  return ERROR_REPORTING_CONFIG.enabled;
}

/**
 * Get the developer email for notifications.
 * Returns null if not configured.
 *
 * @returns Developer email or null
 */
export function getDevEmail(): string | null {
  const email = ERROR_REPORTING_CONFIG.email.to;
  return email && email.includes('@') ? email : null;
}
